import { useContext, useState, useCallback, useRef } from 'react';
import { apiPost } from './apiPost';
import { AppContext } from 'contexts/AppContext';

/**
 * Data access for the Sessions tab.
 *
 * Connects to /send/get-sessions (one row per conversation) and
 * /send/get-thread (every turn of one conversation). Both landed in
 * monitor-backend PR #12; before them the console could show messages but not
 * conversations, and reading one exchange meant re-joining rows by eye.
 *
 * Two things about these endpoints that shape this hook:
 *
 *  - Paging is done in SQL, not in the browser. The list endpoint caps at 200
 *    per page and the thread at 2000, because these tables live on the EOL
 *    documentai instance and unbounded fetches are off the table. So `offset`
 *    is a real round trip rather than a slice of cached rows.
 *
 *  - get-thread returns `{data, total, limit, offset}`. `total` is the whole
 *    conversation's turn count, not the page's. It has to be surfaced: the
 *    longest real conversation measured is 1,094 turns, so a page cut at the
 *    limit is otherwise indistinguishable from a complete thread and the UI
 *    would render a conversation that silently stops.
 */

const PAGE_SIZE = 50;

export const useSessionsData = () => {
  const {
    dateRange,
    selectedClient,
    getAccessTokenSilently,
    base_url,
    environmentFilter,
    languageFilter,
  } = useContext(AppContext);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Monotonic id per list request. Two fetches can be in flight at once (a
  // filter changing twice quickly, or a click-through's mount + prefill), and
  // without this whichever RESPONSE lands last owns the list — the loser can
  // be the older request, silently showing rows the current filters exclude.
  const listSeq = useRef(0);

  const [thread, setThread] = useState(null);
  const [threadTotal, setThreadTotal] = useState(0);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  // Same treatment as the Quality hook -- see hooks/apiPost.js.
  const post = useCallback(
    (path, body) => apiPost(base_url, getAccessTokenSilently, path, body),
    [base_url, getAccessTokenSilently],
  );

  const fetchSessions = useCallback(async ({ feedback, platform, userId, pageIndex = 0,
                                             excludeInternal = false, internalOnly = false,
                                             modality, category, sort } = {}) => {
    // dateRange is an ARRAY [from, to] of Dates, not {from,to} -- see
    // AppContext.js:152 and its own `dateRange.map(d => d.toISOString())`.
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    if (!from || !to || !selectedClient?.key) return;

    const seq = ++listSeq.current;
    setLoading(true);
    setError(null);
    try {
      const body = {
        start_date: new Date(from).toISOString().split('T')[0],
        end_date: new Date(to).toISOString().split('T')[0],
        client: selectedClient.key,
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        environment: environmentFilter || 'all',
        ...(languageFilter ? { language: languageFilter } : {}),
      };
      // Omitted rather than sent null: the endpoint treats absent as "no filter",
      // and an explicit null would be a different (unvalidated) code path.
      if (feedback) body.feedback = feedback;
      if (platform) body.platform = platform;
      if (userId) body.user_id = userId;
      if (excludeInternal) body.exclude_internal = true;
      if (internalOnly) body.internal_only = true;
      if (modality) body.modality = modality;
      if (category) body.category = category;
      // Newest-first is the endpoint's default; only the deviation is sent.
      if (sort === 'asc') body.sort = 'asc';

      const data = await post('get-sessions', body);
      if (seq !== listSeq.current) return; // a newer request owns the list now
      const rows = data?.data || [];
      setSessions(rows);
      setPage(pageIndex);
      // A full page means there is probably another. The list endpoint returns
      // no total -- unlike get-thread -- so this is the honest signal available.
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err) {
      if (seq !== listSeq.current) return;
      setError(err.response?.data?.detail || err.message || 'Failed to load sessions');
      setSessions([]);
      setHasMore(false);
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  }, [dateRange, selectedClient, environmentFilter, languageFilter, post]);

  const fetchThread = useCallback(async (session) => {
    if (!session?.session_id || !selectedClient?.key) return;

    setActiveSession(session);
    setThreadLoading(true);
    setThreadError(null);
    setThread(null);
    try {
      const data = await post('get-thread', {
        session_id: session.session_id,
        user_id: session.user_id,
        client: selectedClient.key,
        limit: 500,
      });
      setThread(data?.data || []);
      setThreadTotal(data?.total ?? (data?.data?.length || 0));
    } catch (err) {
      setThreadError(err.response?.data?.detail || err.message || 'Failed to load conversation');
      setThread([]);
      setThreadTotal(0);
    } finally {
      setThreadLoading(false);
    }
  }, [selectedClient, post]);

  const clearThread = useCallback(() => {
    setActiveSession(null);
    setThread(null);
    setThreadTotal(0);
    setThreadError(null);
  }, []);

  return {
    sessions, loading, error, page, hasMore, pageSize: PAGE_SIZE, fetchSessions,
    thread, threadTotal, threadLoading, threadError, activeSession, fetchThread, clearThread,
  };
};

export default useSessionsData;
